/**
 * CurlyCubs motion system — scroll reveals and hand-drawn line draws.
 *
 * Sections/blocks opt in with `data-cc-reveal` (optionally
 * `data-cc-reveal="fade|scale|rise|mask"`) and, for staggered groups,
 * `data-cc-reveal-group` on the parent. This module only ever toggles the
 * `is-revealed` class and sets small CSS custom properties — all the actual
 * animation lives in curlycubs-motion.css as transform/opacity transitions.
 *
 * Respects prefers-reduced-motion by skipping the observer entirely and
 * marking everything revealed up front, and re-scans the page when new
 * markup is injected (theme editor block changes, section re-renders).
 */

const revealSelector = '[data-cc-reveal]';
const groupSelector = '[data-cc-reveal-group]';
const drawPathSelector = '.cc-draw-path';

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** @type {IntersectionObserver | null} */
let revealObserver = null;

function getRevealObserver() {
  if (revealObserver) return revealObserver;

  revealObserver = new IntersectionObserver(
    (entries, observer) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-revealed');
        observer.unobserve(entry.target);
      }
    },
    { rootMargin: '0px 0px -10% 0px', threshold: 0.15 }
  );

  return revealObserver;
}

function indexStaggerGroups(root) {
  const groups = root.matches?.(groupSelector) ? [root] : [...root.querySelectorAll(groupSelector)];

  for (const group of groups) {
    const children = group.querySelectorAll(`:scope > ${revealSelector}`);
    children.forEach((child, index) => {
      child.style.setProperty('--cc-i', String(index));
    });
  }
}

function measureDrawPaths(root) {
  const paths = root.matches?.(drawPathSelector) ? [root] : [...root.querySelectorAll(drawPathSelector)];

  for (const path of paths) {
    if (typeof path.getTotalLength !== 'function') continue;
    if (path.dataset.ccMeasured) continue;

    try {
      const length = Math.ceil(path.getTotalLength());
      path.style.setProperty('--cc-path-length', String(length));
      path.dataset.ccMeasured = 'true';
    } catch {
      // Path not renderable yet (e.g. display: none ancestor) — leave the
      // CSS fallback length in place, it will look correct enough.
    }
  }
}

function revealImmediately(root) {
  const targets = root.matches?.(revealSelector) ? [root] : [...root.querySelectorAll(revealSelector)];
  for (const target of targets) target.classList.add('is-revealed');
}

/**
 * Scans `root` for reveal targets and draw-paths and wires them up.
 * Safe to call repeatedly on the same root — already-revealed or
 * already-measured elements are skipped.
 * @param {ParentNode & Element} [root]
 */
export function observeReveals(root = document.body) {
  measureDrawPaths(root);
  indexStaggerGroups(root);

  if (prefersReducedMotion()) {
    revealImmediately(root);
    return;
  }

  const observer = getRevealObserver();
  const targets = root.matches?.(revealSelector) ? [root] : [...root.querySelectorAll(revealSelector)];

  for (const target of targets) {
    if (target.classList.contains('is-revealed')) continue;
    observer.observe(target);
  }
}

function init() {
  observeReveals(document.body);

  // Theme editor / dynamically-injected sections: re-scan whichever subtree
  // changed instead of the whole document.
  const bodyObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        observeReveals(node);
      }
    }
  });

  bodyObserver.observe(document.body, { childList: true, subtree: true });

  // Belt-and-suspenders for the Shopify theme editor's own section-reload event.
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof Element) observeReveals(event.target);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
